import {
  planLabel,
  planLimitMessage,
  PRICES,
  roleLabel,
  TEAM_MIN_EDITORS,
  type GuideSummary,
  type MeResponse,
  type Role,
  type WorkspaceSummary,
} from '@nolens/domain';
import { formatDate, formatNumber, type Locale } from '@nolens/i18n';
import { Banner, Button, EmptyState, Field, Input, useLocale } from '@nolens/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AppShell, type Crumb } from '../components/AppShell';
import { GuideCollection, useLibraryView, ViewToggle } from '../components/GuideCard';
import { canImportGuide, ImportGuideButton } from '../components/ImportDialog';
import { SearchIcon } from '../components/Icons';
import { QueryView } from '../components/Query';
import { SpaceTile } from '../components/SpaceTile';
import { ApiError } from '../lib/api';
import { detectExtension } from '../lib/extension';
import { spaceName } from '../lib/locale';
import { useCreateTeam, useGuides, useMe, useWorkspacePlan } from '../lib/queries';
import { workspaceText } from '../i18n/workspace';
import { eur, publicPaths } from '../i18n/landing';

/**
 * Biblioteca vazia: o que fazer a seguir depende de onde a pessoa está. Sem a extensão,
 * instalá-la; instalada mas ligada a outra conta (ou a nenhuma), ligá-la; ligada a esta conta,
 * clicar no ícone e gravar. Numa equipa, os guias chegam do espaço pessoal (no guia, «Espaço»);
 * quem só vê, espera que alguém os traga.
 */
export function EmptyLibrary({
  kind,
  role,
  email,
}: {
  kind: 'personal' | 'team';
  role: Role;
  email: string | null;
}) {
  const t = workspaceText(useLocale());
  const ext = useQuery({
    queryKey: ['extension'],
    queryFn: () => detectExtension(),
    staleTime: 0,
    gcTime: 0,
  });
  if (kind === 'team' && role === 'viewer')
    return <EmptyState title={t.noGuides} description={t.noGuidesViewer} />;
  const state = ext.isPending
    ? null
    : !ext.data
      ? 'missing'
      : ext.data.linkedEmail !== email
        ? 'unlinked'
        : 'ready';
  const hint =
    kind === 'team'
      ? t.noGuidesTeam
      : state === 'missing'
        ? t.noGuidesInstall
        : state === 'unlinked'
          ? t.noGuidesLink
          : state === 'ready'
            ? t.noGuidesReady
            : t.noGuidesHint;
  return (
    <div data-empty-library={state ?? 'checking'}>
      <EmptyState
        title={t.noGuides}
        description={hint}
        action={
          state === 'missing' || state === 'unlinked' ? (
            <Link to="/extensao">
              <Button variant="secondary">
                {state === 'missing' ? t.installExtension : t.connectExtension}
              </Button>
            </Link>
          ) : undefined
        }
      />
    </div>
  );
}

/** O espaço como primeiro passo do caminho na barra de topo, seguido do resto. */
export function spaceCrumbs(
  me: MeResponse,
  id: string | undefined,
  locale: Locale,
  ...rest: Crumb[]
): Crumb[] {
  const ws = me.workspaces.find((w) => w.id === id);
  return ws ? [{ label: spaceName(ws, locale), to: `/w/${ws.id}`, space: ws }, ...rest] : rest;
}

/** Com a conta carregada (barra lateral, caminho na barra de topo). */
export function WithMe({
  children,
  current,
  crumbs,
  settings,
}: {
  children: (me: MeResponse) => ReactNode;
  current?: string;
  crumbs?: (me: MeResponse) => Crumb[];
  settings?: boolean;
}) {
  const me = useMe();
  return (
    <QueryView q={me}>
      {(data) => (
        <AppShell me={data} current={current} crumbs={crumbs?.(data)} settings={settings}>
          {children(data)}
        </AppShell>
      )}
    </QueryView>
  );
}

export function WorkspacePage() {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = workspaceText(locale);
  return (
    <WithMe current={workspaceId}>
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws) return <EmptyState title={t.spaceNotFound} description={t.spaceNotFoundHint} />;
        return <GuideLibrary ws={ws} email={me.user.email} canImport={canImportGuide(me)} />;
      }}
    </WithMe>
  );
}

/** Procurar na lista (título e descrição). */
export function LibrarySearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const t = workspaceText(useLocale());
  return (
    <label className="relative block min-w-0 flex-1">
      <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
      <input
        type="search"
        aria-label={t.searchGuides}
        placeholder={t.searchPlaceholder}
        className="h-10 w-full rounded-full bg-surface-1 pl-10 pr-4 text-[14.5px] shadow-[inset_0_0_0_1px_var(--border)] outline-none focus:shadow-[inset_0_0_0_2px_var(--accent)]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export const matches = (g: GuideSummary, q: string) => {
  const needle = q.trim().toLowerCase();
  return !needle || (g.title + ' ' + g.description).toLowerCase().includes(needle);
};

function GuideLibrary({
  ws,
  email,
  canImport,
}: {
  ws: WorkspaceSummary;
  email: string | null;
  canImport: boolean;
}) {
  const locale = useLocale();
  const t = workspaceText(locale);
  const guides = useGuides(ws.id);
  const [q, setQ] = useState('');
  const [view, setView] = useLibraryView();
  return (
    <section>
      <div className="flex items-center gap-4">
        <SpaceTile ws={ws} size={44} />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-text-3">
            {ws.kind === 'team' ? t.team : t.personalSpace}
          </p>
          <h1 className="truncate text-2xl font-semibold tracking-tight">
            {spaceName(ws, locale)}
          </h1>
          <p className="mt-0.5 text-sm text-text-2">
            {ws.kind === 'personal'
              ? t.onlyYou
              : t.teamLine(ws.members, roleLabel(ws.role, locale))}
          </p>
        </div>
      </div>
      <PlanLine workspaceId={ws.id} />
      <div className="mt-6 flex items-center gap-3">
        <LibrarySearch value={q} onChange={setQ} />
        <ViewToggle value={view} onChange={setView} />
        {canImport && ws.role !== 'viewer' && <ImportGuideButton workspaceId={ws.id} />}
      </div>
      <div className="mt-5">
        <QueryView q={guides}>
          {(list) => {
            const shown = list.filter((g) => matches(g, q));
            if (!list.length) return <EmptyLibrary kind={ws.kind} role={ws.role} email={email} />;
            if (!shown.length) return <p className="text-text-2">{t.noMatch(q)}</p>;
            return <GuideCollection guides={shown} view={view} />;
          }}
        </QueryView>
      </div>
    </section>
  );
}

/**
 * Nova equipa. Numa conta grátis é a entrada no plano Equipa: a equipa nasce sem plano e
 * segue para a subscrição (só fica ativa quando a subscrição começa).
 */
export function NewTeamPage() {
  const create = useCreateTeam();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const locale = useLocale();
  const t = workspaceText(locale);
  return (
    <WithMe crumbs={() => [{ label: t.newTeam }]}>
      {(me) => {
        const free = me.workspaces.find((w) => w.id === me.personalWorkspaceId)?.plan === 'free';
        const submit = (e: FormEvent) => {
          e.preventDefault();
          create.mutate(name.trim(), {
            onSuccess: (ws) =>
              navigate(free ? `/w/${ws.id}/definicoes/plano` : `/w/${ws.id}/membros`),
          });
        };
        return (
          <form
            onSubmit={submit}
            className="flex max-w-md flex-col gap-5"
            data-free={free || undefined}
          >
            {free ? (
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t.teamPlan}</h1>
                <p className="mt-1 text-sm text-text-2">
                  {t.teamPlanHint(
                    eur(locale, TEAM_MIN_EDITORS * PRICES.team.monthly),
                    TEAM_MIN_EDITORS,
                  )}
                </p>
                <p className="mt-2 text-sm text-text-2">
                  {t.teamPlanNext}{' '}
                  <Link to={publicPaths(locale).pricing} className="underline underline-offset-2">
                    {t.comparePlans}
                  </Link>
                </p>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t.newTeam}</h1>
                <p className="mt-1 text-sm text-text-2">{t.newTeamHint}</p>
              </div>
            )}
            <Field
              label={t.teamName}
              error={create.error instanceof ApiError ? create.error.message : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.teamNamePlaceholder}
                />
              )}
            </Field>
            <Button type="submit" loading={create.isPending}>
              {free ? t.continueToSubscribe : t.createTeam}
            </Button>
          </form>
        );
      }}
    </WithMe>
  );
}

/** Plano do espaço e o uso (guias, capturas); avisa quando a equipa não tem plano ativo. */
export function PlanLine({ workspaceId, usage = true }: { workspaceId: string; usage?: boolean }) {
  const q = useWorkspacePlan(workspaceId);
  const locale = useLocale();
  const t = workspaceText(locale);
  const pricingPath = publicPaths(locale).pricing;
  const p = q.data;
  if (!p) return null;
  const until = p.trialEndsAt ? formatDate(locale, p.trialEndsAt, {}) : null;
  return (
    <>
      {p.plan === 'expired' && (
        <Banner tone="attention" className="mt-4">
          {planLimitMessage('expired', locale)}
          {/* Na página do plano, a subscrição está logo a seguir */}
          {usage && (
            <>
              {' '}
              <Link to={`/w/${workspaceId}/definicoes/plano`} className="font-medium underline">
                {t.seePlans}
              </Link>
            </>
          )}
        </Banner>
      )}
      <p
        className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-text-3"
        data-plan={p.plan}
      >
        <span className="rounded-full bg-surface-2 px-2.5 py-0.5 font-medium text-text-2">
          {planLabel(p.plan, locale)}
          {p.status === 'trialing' && until ? t.trialUntil(until) : ''}
        </span>
        {usage && p.features.guides !== null && (
          <span>{t.guidesUsage(p.usage.guides, p.features.guides)}</span>
        )}
        {usage && (
          <span>
            {t.storageUsage(
              formatNumber(locale, p.usage.storageMb),
              formatNumber(locale, p.storageLimitMb / 1024),
            )}
          </span>
        )}
        {usage && p.plan !== 'team' && (
          <Link to={pricingPath} className="font-medium text-accent-text hover:underline">
            {t.seePlans}
          </Link>
        )}
      </p>
    </>
  );
}
